import type { Tour } from '@/types/tour';
import { currentBuildingId, currentFloorId, currentRoomId } from '@/core/state';
import { findRoomLocation } from '@/core/tour-utils';
import { mediaUrl } from '@/core/tour-loader';

// Schlanker Header: links Brand-Mark + Tour-Titel, rechts der aktuelle
// Standort-Pfad (Building > Floor > Room). Aktualisiert sich automatisch.

export class AppHeader {
  private el: HTMLElement;
  private locationEl: HTMLElement;

  constructor(parent: HTMLElement, private tour: Tour) {
    this.el = parent;
    this.el.classList.add('app-header');
    const subtitle = tour.theme?.headerSubtitle?.trim() || tour.meta.location;
    const initials = brandInitials(tour.meta.title);
    const brand = tour.theme?.logoUrl
      ? `<span class="app-header__mark app-header__mark--image" aria-hidden="true">
           <img src="${escapeHtml(mediaUrl(tour.theme.logoUrl))}" alt="" />
         </span>`
      : `<span class="app-header__mark" aria-hidden="true">${escapeHtml(initials)}</span>`;
    this.el.innerHTML = `
      <button type="button" class="app-header__burger" data-role="burger" aria-label="Navigation oeffnen">
        <span></span><span></span><span></span>
      </button>
      <div class="app-header__brand">
        ${brand}
        <div class="app-header__titles">
          <span class="app-header__title">${escapeHtml(this.tour.meta.title)}</span>
          <span class="app-header__sub">${escapeHtml(subtitle)}</span>
        </div>
      </div>
      <div class="app-header__location" aria-live="polite"></div>
    `;

    this.el.querySelector<HTMLButtonElement>('[data-role="burger"]')?.addEventListener('click', () => {
      const open = document.body.dataset.sidebarMobileOpen === 'true';
      if (open) delete document.body.dataset.sidebarMobileOpen;
      else document.body.dataset.sidebarMobileOpen = 'true';
    });

    // Backdrop-Klick (auf das ::after Pseudo-Element kann man nicht
    // direkt klicken, also nutzen wir den Document-Klick und schliessen,
    // wenn der User ausserhalb der Sidebar/Buttons klickt).
    document.addEventListener('click', (e) => {
      if (document.body.dataset.sidebarMobileOpen !== 'true') return;
      const target = e.target as HTMLElement;
      if (target.closest('.app-sidebar') || target.closest('[data-role="burger"]')) return;
      delete document.body.dataset.sidebarMobileOpen;
    });
    const loc = this.el.querySelector<HTMLElement>('.app-header__location');
    if (!loc) throw new Error('Header: Location-Slot fehlt');
    this.locationEl = loc;

    const refresh = (): void => this.renderLocation();
    currentBuildingId.subscribe(refresh);
    currentFloorId.subscribe(refresh);
    currentRoomId.subscribe(refresh);
    refresh();
  }

  private renderLocation(): void {
    const loc = findRoomLocation(this.tour, currentRoomId.get());
    if (!loc) {
      this.locationEl.textContent = '';
      return;
    }
    this.locationEl.innerHTML = `
      <span>${escapeHtml(loc.building.shortName)}</span>
      <span class="app-header__sep" aria-hidden="true">/</span>
      <span>${escapeHtml(loc.floor.shortName)}</span>
      <span class="app-header__sep" aria-hidden="true">/</span>
      <span class="app-header__room">${escapeHtml(loc.room.name)}</span>
    `;
  }
}

// Liefert max. 3 Initialen aus dem Tour-Titel als Brand-Mark-Text.
// "Standort Musterstadt" -> "SM"; "Tour" -> "T"; "Foo Bar Baz" -> "FBB".
function brandInitials(title: string): string {
  const t = title.trim();
  if (!t) return '·';
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    const upper = words[0].replace(/[^A-Za-z]/g, '');
    return upper.slice(0, 3).toUpperCase() || words[0].slice(0, 3).toUpperCase();
  }
  return words.slice(0, 3).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
